import {
  CHAMPIONSHIP_IDS,
  DEFAULT_CHAMPIONSHIP_ID,
  DEFAULT_VISIBILITY_HOURS,
  VISIBILITY_HOURS,
  MAX_QUIZ_OPTIONS,
  MIN_QUIZ_OPTIONS,
  QUESTION_TITLE_MAX_LENGTH,
  QUESTION_TITLE_MIN_LENGTH,
  TRANSLATED_LANGUAGES,
  createInitialAnswersValues,
  createQuizOption,
} from './constants';
import {
  AnswersValues,
  BonusQuestion,
  BonusQuestionPayload,
  BonusQuestionSubmitStatus,
  BonusQuestionType,
  BonusQuestionUpdatePayload,
  DiffusionValues,
  LocalizedText,
  QuestionBonusChampionshipId,
  QuestionBonusVisibilityHours,
  QuestionTitleValues,
  TranslationLanguage,
} from './types';
import {countCharacters} from './utils';

const toNumber = (value: string) => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export interface FormValues {
  diffusion: DiffusionValues;
  questionType: BonusQuestionType;
  questionTitle: QuestionTitleValues;
  answers: AnswersValues;
}

export const buildBonusQuestionPayload = (
  {diffusion, questionType, questionTitle, answers}: FormValues,
  status: BonusQuestionSubmitStatus,
): BonusQuestionPayload => {
  const payload: BonusQuestionPayload = {
    championshipId: diffusion.championshipId,
    gameWeekNumber: diffusion.gameWeekNumber,
    status,
    questionType: questionType,
    title: {
      'fr-FR': questionTitle.fr,
      'en-GB': questionTitle.en,
      'es-ES': questionTitle.es,
    },
    visibilityHoursBeforeGameWeekStart:
      diffusion.visibilityHoursBeforeGameWeekStart,
  };

  // Une seule des deux branches part : celle du `questionType` retenu.
  if (questionType === 'quiz') {
    payload.quiz = {
      options: answers.quiz.options.map(option => ({
        label: {
          'fr-FR': option.label.fr,
          'en-GB': option.label.en,
          'es-ES': option.label.es,
        },
        points: toNumber(option.points),
      })),
    };
  } else {
    const {championshipMatchId, quotations} = answers.matchForecast;
    payload.matchForecast = {
      championshipMatchId,
      quotations: {
        home: toNumber(quotations.home),
        draw: toNumber(quotations.draw),
        away: toNumber(quotations.away),
      },
    };
  }

  return payload;
};

/**
 * Même payload, moins la portée (championnat + journée) que le back traite comme
 * immuable et rejette explicitement sur le PATCH.
 */
export const buildBonusQuestionUpdatePayload = (
  formValues: FormValues,
  status: BonusQuestionSubmitStatus,
): BonusQuestionUpdatePayload => {
  const {championshipId, gameWeekNumber, ...updatablePayload} =
    buildBonusQuestionPayload(formValues, status);
  return updatablePayload;
};

/** Le brouillon accepte l'incomplet ; "Programmer" exige tous les champs. */
const hasTranslations = (text: QuestionTitleValues) =>
  TRANSLATED_LANGUAGES.every(language => text[language].trim() !== '');

export const isBonusQuestionComplete = ({
  diffusion,
  questionType,
  questionTitle,
  answers,
}: FormValues) => {
  if (!diffusion.gameWeekNumber) return false;

  // Le back impose la longueur du titre dans chaque langue, pas seulement en FR.
  const languages: TranslationLanguage[] = ['fr', ...TRANSLATED_LANGUAGES];
  const hasValidTitleLength = languages.every(language => {
    const titleLength = countCharacters(questionTitle[language]);
    return (
      titleLength >= QUESTION_TITLE_MIN_LENGTH &&
      titleLength <= QUESTION_TITLE_MAX_LENGTH
    );
  });
  if (!hasValidTitleLength) return false;

  if (questionType === 'quiz') {
    const {options} = answers.quiz;
    return (
      options.length >= MIN_QUIZ_OPTIONS &&
      options.length <= MAX_QUIZ_OPTIONS &&
      options.every(
        option =>
          option.label.fr.trim() !== '' &&
          hasTranslations(option.label) &&
          option.points !== '',
      )
    );
  }

  const {championshipMatchId, quotations} = answers.matchForecast;
  return (
    Boolean(championshipMatchId) &&
    quotations.home !== '' &&
    quotations.draw !== '' &&
    quotations.away !== ''
  );
};

/**
 * L'API renvoie des `number` bruts là où le formulaire n'accepte que les valeurs
 * proposées : on retombe sur les défauts si le back sort de la liste.
 */
const toChampionshipId = (value?: number): QuestionBonusChampionshipId =>
  CHAMPIONSHIP_IDS.find(id => id === value) || DEFAULT_CHAMPIONSHIP_ID;

const toVisibilityHours = (value?: number): QuestionBonusVisibilityHours =>
  VISIBILITY_HOURS.find(hours => hours === value) || DEFAULT_VISIBILITY_HOURS;

const fromLocalizedText = (text?: LocalizedText): QuestionTitleValues => ({
  fr: text?.['fr-FR'] || '',
  en: text?.['en-GB'] || '',
  es: text?.['es-ES'] || '',
});

/** Inverse de `buildBonusQuestionPayload`, pour les écrans Modifier et Voir. */
export const toFormValues = (bonusQuestion: BonusQuestion): FormValues => {
  const initialAnswers = createInitialAnswersValues();

  const quizOptions = bonusQuestion.quiz?.options?.length
    ? bonusQuestion.quiz.options.map(option => ({
        ...createQuizOption(),
        label: fromLocalizedText(option.label),
        points: option.points === undefined ? '' : String(option.points),
      }))
    : initialAnswers.quiz.options;

  const quotations = bonusQuestion.matchForecast?.quotations;

  return {
    diffusion: {
      championshipId: toChampionshipId(bonusQuestion.championshipId),
      gameWeekNumber: bonusQuestion.gameWeekNumber,
      visibilityHoursBeforeGameWeekStart: toVisibilityHours(
        bonusQuestion.visibilityHoursBeforeGameWeekStart,
      ),
    },
    questionType: bonusQuestion.questionType || 'quiz',
    questionTitle: fromLocalizedText(bonusQuestion.title),
    answers: {
      quiz: {options: quizOptions},
      matchForecast: {
        championshipMatchId: bonusQuestion.matchForecast?.championshipMatchId,
        quotations: quotations
          ? {
              home: String(quotations.home),
              draw: String(quotations.draw),
              away: String(quotations.away),
            }
          : initialAnswers.matchForecast.quotations,
      },
    },
  };
};



// WEBPACK FOOTER //
// ./src/pages/Other/MPP/QuestionBonus/payload.ts